import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../Config";

// Formats "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm:ss.sssZ") as "DD-MM-YYYY".
// String-based rather than `new Date(...)` + local getters, so it can't be
// shifted by a day depending on the browser's timezone.
const formatDate = (value) => {
  if (!value) return "-";
  const datePart = String(value).split("T")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
};

// Shows money as ₹66500.00 (display only; leaves non-numeric values untouched)
const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isNaN(n) ? `₹${value}` : `₹${n.toFixed(2)}`;
};
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// "2026-02-28" (or "2026-02-28T00:00:00.000Z") -> "28 February 2026"
const formatDateLong = (date) => {
  if (!date) return "";
  const [year, month, day] = String(date).split("T")[0].split("-");
  if (!year || !month || !day) return date;
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
};
// invoiceid: the cashback invoice to load and display (falls back to the
// :invoiceid route param so this works whether passed as a prop or rendered
// straight from a route)
const CashbackView = ({ invoiceid: invoiceidProp }) => {
  const navigate = useNavigate();

  const { invoiceid: invoiceidParam } = useParams();
  const invoiceid = invoiceidProp || invoiceidParam;

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    if (!data) return;

    // Slashes -> dashes (invoice numbers like "CB/26/001" stay readable),
    // drop other invalid filename characters, spaces -> underscores
    const clean = (s) =>
      String(s ?? "")
        .trim()
        .replace(/[\\/]+/g, "-")
        .replace(/[:*?"<>|]+/g, "")
        .replace(/\s+/g, "_");

    const previousTitle = document.title;
    document.title = ["cashback", clean(data.invoiceno), clean(data.guestname)]
      .filter(Boolean)
      .join("_");

    return () => {
      document.title = previousTitle;
    };
  }, [data]);
  useEffect(() => {
    if (!invoiceid) {
      setIsLoading(false);
      return;
    }

    const fetchInvoice = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(
          `${API_URL}/billing/cashback-invoice/${invoiceid}`,
        );
        const result = await res.json();

        if (!res.ok || !result.success) {
          setLoadError(result.message || "Failed to load cashback invoice.");
          return;
        }

        const inv = result.invoice;
        setData({
          invoiceno: inv.invoiceno,
          bookingid: inv.bookingid,
          invoicedate: inv.invoicedate,
          guestname: inv.guestname,
          gstno: inv.gstno,
          country: inv.country,
          rows: inv.items.map((item) => ({
            particulars: item.particulars,
            from: item.checkin,
            to: item.checkout,
            nights: item.nights,
            amount: item.amount,
            cashback: item.cashback,
          })),
          amountinwords: inv.amountinwords,
          totalAmount: inv.total,
        });
      } catch (err) {
        console.error("Error loading cashback invoice:", err);
        setLoadError(
          "Something went wrong while loading the cashback invoice.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceid]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-10 text-center text-gray-600">
        Loading cashback invoice...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto p-10 text-center text-red-600">
        {loadError}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-4xl mx-auto p-10 text-center text-gray-600">
        No cashback invoice to display.
      </div>
    );
  }

  const {
    invoiceno,
    invoicedate,
    guestname,
    gstno,
    country,
    rows,
    amountinwords,
    totalAmount,
  } = data;

  const label = "text-[10px] tracking-wide text-[#7b807a] uppercase";

  return (
    <div className="cb-invoice max-w-4xl mx-auto px-12 py-12 bg-white text-[#1c2420] print:p-0">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
        .cb-invoice { font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif; }
        .cb-title { font-weight: 500; letter-spacing: -0.04em; line-height: 0.88; }
        @media print {
          @page { margin: 0; }
          body { margin: 1.6cm; }
          .no-print { display: none; }
        }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-start">
        <h1 className="cb-title text-[76px] uppercase">
          Cashback
          <br />
          Invoice
        </h1>
        <div className="w-28 h-28 shrink-0">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mt-10 pb-6 border-b border-[#1c2420]">
        <div>
          <p className={label}>Invoice No.</p>
          <p className="mt-1 text-sm font-bold">{invoiceno}</p>
        </div>
        <div>
          <p className={label}>Date</p>
          <p className="mt-1 text-sm font-bold">
            {formatDateLong(invoicedate)}
          </p>
        </div>
      </div>

      {/* Bill To / From */}
      <div className="flex justify-between items-start py-6 border-b border-[#1c2420]">
        <div className="w-1/2 text-xs tracking-wider leading-[1.9] text-[#3b413a]">
          <p className={label}>Bill To</p>
          <p className="mt-3 text-base font-bold text-[#1c2420] leading-normal">
            {guestname}
          </p>
          {gstno && <p>GSTIN NO.: {gstno}</p>}
          {/* {country && <p>{country}</p>} */}
          {country && (
            <p
              style={{
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                maxWidth: "100%",
              }}
            >
              {country}
            </p>
          )}
        </div>
        <div className="text-right text-xs tracking-wider leading-[1.9] text-[#3b413a]">
          <p className={label}>From</p>
          <p className="mt-3 text-base font-bold text-[#1c2420] leading-normal">
            Zorba Stays
          </p>
          <p>GSTIN NO.: 24AADFZ2976K1ZO</p>
          <p>306-7 - Business Centrum,</p>
          <p>Amin Marg,</p>
          <p>Rajkot, 360001</p>
          <p>India</p>
        </div>
      </div>

      {/* Table */}
      <table className="w-full border-collapse text-xs mt-8">
        <thead>
          <tr className="text-[10px] uppercase text-[#7b807a] text-left align-bottom">
            <th className="pb-4 font-normal w-[50px]">
              Sr.
              {/* <br /> */}
              No.
            </th>
            <th className="pb-4 font-normal w-[34%]">Particulars</th>
            <th className="pb-4 font-normal w-[26%]">Period of stay</th>
            <th className="pb-4 font-normal text-center">No. of nights</th>
            <th className="pb-4 font-normal text-right">Bill amount</th>
            <th className="pb-4 font-normal text-right">Cashback</th>
          </tr>
        </thead>
        <tbody className="border-t border-[#1c2420]">
          {rows?.map((row, index) => (
            <tr
              key={index}
              className="border-b border-[#1c2420] align-top leading-relaxed"
            >
              <td className="py-4">{index + 1}</td>
              <td className="py-4 pr-4 whitespace-pre-line break-words">
                {row.particulars}
              </td>
              <td className="py-4 pr-2">
                {row.from && row.to
                  ? `${formatDate(row.from)} - ${formatDate(row.to)}`
                  : "-"}
              </td>
              <td className="py-4 text-center">{row.nights}</td>
              <td className="py-4 text-right">{formatMoney(row.amount)}</td>
              <td className="py-4 text-right">{formatMoney(row.cashback)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Total */}
      <div className="flex justify-end items-center py-4 border-b border-[#1c2420] text-xs font-bold">
        <span className="mr-24">Total</span>
        <span className="w-28 text-right">{formatMoney(totalAmount)}</span>
      </div>

      {/* In Words */}
      <div className="mt-8">
        <p className={label}>In Words</p>
        <p className="mt-1 text-sm font-bold">{amountinwords}</p>
      </div>

      {/* Thank you */}
      <div className="mt-6 text-sm font-bold leading-snug">
        <p>Thank you!</p>
        <p>It’s been a pleasure hosting you!</p>
      </div>

      {/* Cancellation + Payment */}
      <div className="mt-10 grid grid-cols-2 gap-10">
        <div>
          <p className={label}>Cancellation Policy</p>
          <ul className="mt-4 text-xs leading-relaxed text-[#3b413a] list-disc pl-5 space-y-1 w-4/5">
            <li>
              30 days prior to the check-in date, 100% payment refundable.
            </li>
            <li>
              If cancellation is not made before the 30 days then advance amount
              will be forfeited.
            </li>
          </ul>
        </div>
        <div>
          <p className={label}>Payment Info</p>
          <div className="mt-4 text-xs leading-relaxed text-[#3b413a] space-y-0.5">
            <p>HDFC Bank</p>
            <p>Ac NO.: 50200077506738</p>
            <p>Ac Name: ZORBA STAYS</p>
            <p>IFSC: HDFC0000101</p>
            <p>Branch: RAJKOT - YAGNIK ROAD - GUJARAT</p>
            <p>Account Type: CURRENT</p>
          </div>
          <div className="mt-4 flex items-end gap-3">
            <img
              src="https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public"
              alt="UPI QR Code - Zorba Stays"
              className="w-24 h-24 object-contain"
            />
            <div className="text-xs text-[#3b413a] leading-relaxed">
              <p>Name: ZORBA STAYS</p>
              <p>UPI ID: 8866262368@hdfcbank</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-14 flex items-center gap-3">
        <div className="flex-1 border-t border-[#1c2420]"></div>
        <img
          src="https://dash.zorbastays.com/assets/zorba_logo.webp"
          alt="Zorba Stays"
          className="w-5 h-5 object-contain"
        />
        <div className="flex-1 border-t border-[#1c2420]"></div>
      </div>
      <div className="mt-4 flex justify-between text-[10px] text-[#3b413a]">
        <span>@zorba_stays</span>
        <span>www.zorbastays.com</span>
      </div>

      {/* Print Button */}
      <div className="flex justify-center gap-4 mt-10 mb-4 no-print">
        <button
          onClick={() => window.print()}
          className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
        >
          Print Invoice
        </button>
        <button
          onClick={() => navigate("/bills")}
          className="px-6 py-2 rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 transition-all duration-300 text-sm"
        >
          Back to Bills
        </button>
      </div>
    </div>
  );
};

export default CashbackView;
// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import { GoPlusCircle } from "react-icons/go";
// import { FiMinusCircle } from "react-icons/fi";
// import { useNavigate } from "react-router-dom";
// import { API_URL } from "../../Config";

// // Formats "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm:ss.sssZ") as "DD-MM-YYYY".
// // String-based rather than `new Date(...)` + local getters, so it can't be
// // shifted by a day depending on the browser's timezone.
// const formatDate = (value) => {
//   if (!value) return "-";
//   const datePart = String(value).split("T")[0];
//   const [year, month, day] = datePart.split("-");
//   if (!year || !month || !day) return value;
//   return `${day}-${month}-${year}`;
// };

// // invoiceid: the cashback invoice to load and display (falls back to the
// // :invoiceid route param so this works whether passed as a prop or rendered
// // straight from a route)
// const CashbackView = ({ invoiceid: invoiceidProp }) => {
//   const navigate = useNavigate();

//   const { invoiceid: invoiceidParam } = useParams();
//   const invoiceid = invoiceidProp || invoiceidParam;

//   const [data, setData] = useState(null);
//   const [isLoading, setIsLoading] = useState(true);
//   const [loadError, setLoadError] = useState(null);

//   useEffect(() => {
//     if (!invoiceid) {
//       setIsLoading(false);
//       return;
//     }

//     const fetchInvoice = async () => {
//       try {
//         setIsLoading(true);
//         setLoadError(null);
//         const res = await fetch(
//           `${API_URL}/billing/cashback-invoice/${invoiceid}`,
//         );
//         const result = await res.json();

//         if (!res.ok || !result.success) {
//           setLoadError(result.message || "Failed to load cashback invoice.");
//           return;
//         }

//         const inv = result.invoice;
//         setData({
//           invoiceno: inv.invoiceno,
//           invoicedate: inv.invoicedate,
//           guestname: inv.guestname,
//           rows: inv.items.map((item) => ({
//             particulars: item.particulars,
//             from: item.checkin,
//             to: item.checkout,
//             nights: item.nights,
//             amount: item.amount,
//             cashback: item.cashback,
//           })),
//           amountinwords: inv.amountinwords,
//           totalAmount: inv.total,
//         });
//       } catch (err) {
//         console.error("Error loading cashback invoice:", err);
//         setLoadError(
//           "Something went wrong while loading the cashback invoice.",
//         );
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchInvoice();
//   }, [invoiceid]);

//   if (isLoading) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
//         Loading cashback invoice...
//       </div>
//     );
//   }

//   if (loadError) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
//         {loadError}
//       </div>
//     );
//   }

//   if (!data) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
//         No cashback invoice to display.
//       </div>
//     );
//   }

//   const {
//     invoiceno,
//     invoicedate,
//     guestname,
//     rows,
//     amountinwords,
//     totalAmount,
//   } = data;

//   return (
//     <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
//       <style>{`
//         @media print {
//           @page { margin: 0; }
//           body { margin: 1.6cm; }
//           .no-print { display: none; }
//         }
//       `}</style>

//       {/* Header */}
//       <div className="flex justify-between items-start mb-6">
//         <div>
//           <h1 className="text-4xl font-bold mb-2">Cashback Invoice</h1>
//           <br />
//           <br />
//           <h1 className="nt-seawave text-4xl mb-2 text-black">ZORBA STAYS</h1>
//           <p className="text-sm">
//             306, 7 - Business Centrum <br /> Amin Marg, Rajkot, 360001
//           </p>
//           {/* <p className="text-sm">GSTIN No: 24AADFZ2976K1ZO</p> */}
//         </div>
//         <div className="w-24 h-24">
//           <img
//             src="https://zorbastays.com/android-chrome-512x512.png"
//             alt="Zorba Stays Logo"
//             className="w-full h-full object-contain"
//           />
//         </div>
//       </div>

//       <div className="border-t border-black mb-6"></div>

//       {/* Invoice Meta */}
//       <div className="flex gap-16 mb-8">
//         <div>
//           <p className="text-xs font-bold uppercase">Invoice No.</p>
//           <p className="font-medium">{invoiceno}</p>
//         </div>
//         <div>
//           <p className="text-xs font-bold uppercase">Invoice Date</p>
//           <p className="font-medium">{formatDate(invoicedate)}</p>
//         </div>
//       </div>

//       <div className="mb-8">
//         <p className="font-bold">Invoice To:-</p>
//         <p className="text-lg">{guestname}</p>
//       </div>

//       {/* Table Design */}
//       <table className="w-full border-collapse border-t border-b border-black text-center text-sm">
//         <thead>
//           <tr className="uppercase text-xs font-bold border-b border-black">
//             <th className="py-3 border-black w-[60px]">Sr. No.</th>
//             <th className="py-3">Particulars</th>
//             <th className="py-3">Period of stay</th>
//             <th className="py-3">No. Nights</th>
//             <th className="py-3">Bill Amount</th>
//             <th className="py-3">Cashback</th>
//           </tr>
//         </thead>
//         <tbody>
//           {rows?.map((row, index) => (
//             <tr key={index} className="border-b border-black">
//               <td className="py-3  border-black">{index + 1}</td>
//               <td className="py-3 px-2">{row.particulars}</td>
//               <td className="py-3 px-2">
//                 {row.from && row.to
//                   ? `${formatDate(row.from)} to ${formatDate(row.to)}`
//                   : "-"}
//               </td>
//               <td className="py-3">{row.nights}</td>
//               <td className="py-3">{row.amount}</td>
//               <td className="py-3">{row.cashback}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Totals */}
//       <div className="flex justify-between items-start mt-6 px-2">
//         <div className="w-1/2">
//           <p className="font-bold mb-1">In Words:</p>
//           <p className="uppercase border-b border-black">{amountinwords}</p>
//         </div>
//         <div className="w-48">
//           <div className="flex justify-between py-1 font-bold border-b border-black">
//             <span>Total</span>
//             <span>{totalAmount}</span>
//           </div>
//         </div>
//       </div>
//       {/* Payment Info */}
//       <div className="mt-12 pt-6 border-t border-black">
//         <div className="flex justify-between items-start gap-8">
//           {/* Left: Bank details */}
//           <div className="flex-1">
//             <h2 className="text-2xl font-bold text-[#22261f] mb-2">
//               Payment Info
//             </h2>
//             <div className="text-sm text-gray-700 leading-relaxed space-y-0.5">
//               <p>HDFC Bank</p>
//               <p>Ac NO.: 50200077506738</p>
//               <p>Ac Name: ZORBA STAYS</p>
//               <p>IFSC: HDFC0000101</p>
//               <p>Branch: RAJKOT - YAGNIK ROAD - GUJARAT</p>
//               <p>Account Type: CURRENT</p>
//             </div>
//           </div>

//           {/* Right: QR code + UPI */}
//           <div className="flex flex-col items-end">
//             <img
//               src="https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public"
//               alt="UPI QR Code - Zorba Stays"
//               className="w-40 h-40 object-contain"
//             />
//             <p className="text-sm font-bold text-[#22261f] mt-2 text-right">
//               Name: ZORBA STAYS
//             </p>
//             <p className="text-sm text-gray-700 text-right">
//               UPI ID: 8866262368@hdfcbank
//             </p>
//           </div>
//         </div>
//       </div>

//       <div className="mt-20 text-center text-xs  pt-8">
//         <p>www.zorbastays.com | Contact: +91 63552 31066, +91 98750 28066</p>
//       </div>

//       {/* Print Button */}
//       <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
//         <button
//           onClick={() => window.print()}
//           className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//         >
//           Print Invoice
//         </button>
//         <button
//           onClick={() => navigate("/bills")}
//           className="px-6 py-2 rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 transition-all duration-300 text-sm"
//         >
//           Back to Bills
//         </button>
//       </div>
//     </div>
//   );
// };

// export default CashbackView;
